'use client'

import { ReactNode, useState } from 'react'
import { useIntl } from 'react-intl'
import { Text, InfoBanner } from '@vinted/web-ui'

import { ClickableElement } from 'constants/tracking/clickable-elements'

import { clickEvent } from '@marketplace-web/bundles/items-bundle-data'
import { CurrencyAmountModel, formatCurrencyAmount } from '@marketplace-web/currency/currency-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import OfflineVerificationBuyerModal from '../OfflineVerificationBuyerModal'

type Props = {
  itemVerificationFee?: CurrencyAmountModel | null
}

const ItemVerificationBundleInfoBanner = ({ itemVerificationFee }: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const translate = useTranslate('bundle_items')
  const { track } = useTracking()
  const { locale } = useIntl()

  const handleOfflineVerificationClick = () => {
    setIsModalOpen(true)

    track(clickEvent({ target: ClickableElement.PhysicalAuthenticityBuyer }))
  }

  const handleCloseModal = () => setIsModalOpen(false)

  const values = {
    'how-it-works': (chunks: Array<ReactNode>) => (
      <span key="how-it-works">
        <button type="button" onClick={handleOfflineVerificationClick}>
          <Text
            clickable
            text={chunks}
            type={Text.Type.Subtitle}
            testId="offline-verification-info-banner-link"
            as="span"
          />
        </button>
      </span>
    ),
  }

  if (!itemVerificationFee) return null

  const formattedItemVerificationFee = formatCurrencyAmount(itemVerificationFee, locale)

  return (
    <>
      <InfoBanner
        testId="offline-verification-info-banner"
        styling={InfoBanner.Styling.Tight}
        type={InfoBanner.Type.Warning}
        body={
          <Text
            as="h3"
            text={translate('authenticity_note', values)}
            type={Text.Type.Subtitle}
            theme="amplified"
          />
        }
      />
      <OfflineVerificationBuyerModal
        show={isModalOpen}
        onClose={handleCloseModal}
        verificationFee={formattedItemVerificationFee}
      />
    </>
  )
}

export default ItemVerificationBundleInfoBanner
